import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { searchFormLabels } from './search-form-labels';
import { InputClearIcon } from '../../../advanced-search/icons/input-clear-icon';
import { InputSearchIcon } from '../../../advanced-search/icons/input-search-icon';
import style from '../../../advanced-search/search-form.module.less';
import { MessageDescriptorPropType } from '../../../prop-types/intl';
import QuickSearchFocusHotkey from '../../hotkeys/quicksearch-focus';

const preventDefault = (e) => {
  if (e && e.preventDefault) {
    e.preventDefault();
  }
};

const stopPropagation = (e) => {
  if (e && e.stopPropagation) {
    e.stopPropagation();
  }
};

const SearchFormComponent = ({
  onSearch,
  getSearchTermCount,
  handleSearchInputChange,
  clearSearchInput,
  defaultFormData,
  isHighlighted = false,
  placeholder = null,
  intl,
}) => {
  const { searchTerm } = defaultFormData;
  const inputRef = useRef(null);

  const forceFocusSearchInput = useCallback(() => inputRef.current?.focus(), [inputRef]);
  const handleChange = useCallback(
    (event) => {
      const {
        target: { value },
      } = event;

      handleSearchInputChange(value);
    },
    [handleSearchInputChange],
  );

  const handleClearInput = useCallback(() => {
    clearSearchInput();
  }, [clearSearchInput]);

  const handleQuickSearch = useCallback(() => {
    const trimmedSearchTerm = searchTerm.trim();
    if (searchTerm) {
      getSearchTermCount({ searchTerm: trimmedSearchTerm });
      onSearch({ searchTerm: trimmedSearchTerm });
    }
  }, [searchTerm, getSearchTermCount, onSearch]);

  const handleKeyDown = useCallback(
    (event) => {
      const { key } = event;

      if (key === 'Enter') {
        return handleQuickSearch();
      }
    },
    [handleQuickSearch],
  );

  const handleQuickSearchInputBlur = useCallback(
    (e) => {
      if (isHighlighted && !e.target.value) {
        handleClearInput();
      }
    },
    [isHighlighted, handleClearInput],
  );

  const renderSearchBox = () => {
    const wrapperClassName = classnames(style['search-box-wrapper'], {
      [style.highlighted]: isHighlighted,
    });

    const placeholderText = (placeholder && intl.formatMessage(placeholder)) || intl.formatMessage(searchFormLabels.searchValue);
    return (
      <div className={style['search-expander']}>
        <div className={wrapperClassName} onClick={stopPropagation}>
          <QuickSearchFocusHotkey onFocusQuickSearch={forceFocusSearchInput}>
            <input
              ref={inputRef}
              className={style['search-input']}
              name='searchTerm'
              onBlur={handleQuickSearchInputBlur}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholderText}
              type='text'
              value={searchTerm}
            />
          </QuickSearchFocusHotkey>
          {!!searchTerm && (
            <div className={style['cancel-button']} data-testid='cancel-button' onClick={handleClearInput}>
              <InputClearIcon className={style['icon-xmark']} />
            </div>
          )}
          <Button className={style['search-button']} onClick={handleQuickSearch} theme={THEME_CONSTANTS.unstyled}>
            <InputSearchIcon className={style['search-icon']} />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={preventDefault}>
      <div className={style['search-area']}>{renderSearchBox()}</div>
    </form>
  );
};

SearchFormComponent.propTypes = {
  onSearch: PropTypes.func.isRequired,
  getSearchTermCount: PropTypes.func.isRequired,
  handleSearchInputChange: PropTypes.func.isRequired,
  clearSearchInput: PropTypes.func.isRequired,
  defaultFormData: PropTypes.shape({
    searchTerm: PropTypes.string,
    searchWithin: PropTypes.shape({
      id: PropTypes.string.isRequired,
      getText: PropTypes.func.isRequired,
    }),
    dateRange: PropTypes.shape({
      id: PropTypes.string.isRequired,
      getText: PropTypes.func.isRequired,
    }),
  }).isRequired,
  isHighlighted: PropTypes.bool,
  placeholder: MessageDescriptorPropType,
  intl: PropTypes.object.isRequired,
};

export const SearchForm = injectIntl(SearchFormComponent);
